import { useCallback, useEffect, useMemo, useState } from 'react';
import { bffQuery, bffMutation } from '../lib/bffClient';

// «یک مراجعه، یک فاکتور» — PM-0618 (docs/ONE_APPOINTMENT_ONE_INVOICE_PLAN.md,
// build contract §6.1 / §7.1).
//
// A مراجعه (appointment) carries ITEMS: the sessions of the doctors' orders that
// sit in it. Each item is billed on that مراجعه's one invoice, by accounting, off
// clinicalencounter's session events — the desk never writes a line itself. What
// the desk does is say WHICH مراجعه a session sits in («افزودن به این مراجعه»,
// «جلسهٔ بعدی», «رزرو همهٔ جلسات») and how it ended («انجام شد», «انجام نشد»,
// «تاریخ دیگر»). Every status and every charge here is the BFF's.

const PERFORMER_FIELDS = 'type id label isDefault members { type id name active }';

/** One session row, with where it stands on its مراجعه's bill. */
export const SESSION_FIELDS = `
  id sessionNo appointmentId performerId performerType performerName
  recordedBy performedAt outcome billed voidedAt voidReason
  status plannedDate notDoneReason createdAt appointmentAt
  charge invoiceId invoiceNumber invoicePaid
`;

/** What a day row's chip and the checkout need of the item's order. */
const ITEM_ORDER_FIELDS = `
  id patientId description catalogId sessionCount schedulingMode declarationId
  durationMinutes performers { ${PERFORMER_FIELDS} }
`;

const ITEM_FIELDS = `session { ${SESSION_FIELDS} } order { ${ITEM_ORDER_FIELDS} }`;

// The day row's chip needs a session's status and who did it — not its charge:
// `charge` / `appointmentAt` cost the BFF an invoice and an appointment read per
// مراجعه, which a whole day's list should not pay on every re-read. The checkout
// reads the full session (APPOINTMENT_SERVICES) when the money matters.
const DAY_SESSION_FIELDS = `
  id sessionNo appointmentId performerId performerType performerName
  performedAt outcome voidedAt status plannedDate notDoneReason createdAt
`;

// A read of its own, beside the day's appointments rather than inside them: a
// clinicalencounter that cannot answer must not take the day list down with it,
// and a BFF from before PM-0618 refuses the field — then only the chips go.
const DAY_VISIT_SERVICES = `
  query DayVisitServices($date: String) {
    appointments(date: $date) {
      id openServices services { session { ${DAY_SESSION_FIELDS} } order { ${ITEM_ORDER_FIELDS} } }
    }
  }
`;

const APPOINTMENT_SERVICES = `
  query AppointmentServices($appointmentId: ID!) {
    appointmentServices(appointmentId: $appointmentId) { ${ITEM_FIELDS} }
  }
`;

/** One open مراجعه as the BFF answers it (`OpenVisit`) — patientOpenVisits and attachableVisits. */
export const OPEN_VISIT_FIELDS = 'appointmentId dateTime status invoiceIssued providerName';

const PATIENT_OPEN_VISITS = `
  query PatientOpenVisits($patientId: ID!) {
    patientOpenVisits(patientId: $patientId) { ${OPEN_VISIT_FIELDS} }
  }
`;

const PLAN_ALL_SESSIONS = `
  query PlanAllSessions($orderId: ID!, $firstAt: String!, $intervalDays: Int!, $performer: PerformerRefInput!) {
    planAllSessions(orderId: $orderId, firstAt: $firstAt, intervalDays: $intervalDays, performer: $performer) {
      sessionNo at conflict reason
    }
  }
`;

// The writes answer with the order; every caller re-reads its list anyway,
// because a write moves the order's stage, its due and its neighbour's counts.
const ADD_ORDER_TO_VISIT = `
  mutation AddOrderToVisit($orderId: ID!, $appointmentId: ID!) {
    addOrderToVisit(orderId: $orderId, appointmentId: $appointmentId) { id stage nextSessionNo }
  }
`;

const MARK_SESSION_NOT_DONE = `
  mutation MarkSessionNotDone($orderId: ID!, $sessionId: ID, $appointmentId: ID!, $reason: String) {
    markSessionNotDone(orderId: $orderId, sessionId: $sessionId, appointmentId: $appointmentId, reason: $reason) { id stage nextSessionNo }
  }
`;

const ANSWER_OPEN_SERVICES = `
  mutation AnswerOpenServices($appointmentId: ID!, $answers: [OpenServiceAnswerInput!]!, $issue: Boolean) {
    answerOpenServices(appointmentId: $appointmentId, answers: $answers, issue: $issue) {
      appointmentId remaining issued issuePending failures { sessionId code message }
    }
  }
`;

const BOOK_ALL_SESSIONS = `
  mutation BookAllSessions($orderId: ID!, $slots: [SessionSlotInput!]!) {
    bookAllSessions(orderId: $orderId, slots: $slots) { id stage nextSessionNo }
  }
`;

const RESYNC_SESSION_CHARGE = `
  mutation ResyncSessionCharge($sessionId: ID!) {
    resyncSessionCharge(sessionId: $sessionId) { id status charge }
  }
`;

const ref = (p) => (p ? { type: p.type, id: p.id } : undefined);

/** «افزودن به این مراجعه»: books nothing and bills nothing itself (P1.0). */
export async function addOrderToVisit({ orderId, appointmentId }) {
  const d = await bffMutation(ADD_ORDER_TO_VISIT, { orderId, appointmentId });
  return d?.addOrderToVisit;
}

/** «انجام نشد» for one item: the row becomes history, a fresh one waits under its number. */
export async function markSessionNotDone({ orderId, sessionId, appointmentId, reason }) {
  const d = await bffMutation(MARK_SESSION_NOT_DONE, {
    orderId, sessionId: sessionId || null, appointmentId, reason: reason || null,
  });
  return d?.markSessionNotDone;
}

/**
 * Checkout: every open item answered, then the invoice issued when nothing is
 * left open. `answers`: `{ sessionId, orderId, answer, performer?, laterDate?, reason? }`.
 * Failures come back collected, not thrown.
 */
export async function answerOpenServices({ appointmentId, answers, issue = true }) {
  const d = await bffMutation(ANSWER_OPEN_SERVICES, {
    appointmentId,
    issue,
    answers: answers.map((a) => ({
      sessionId: a.sessionId,
      orderId: a.orderId,
      answer: a.answer,
      performer: a.answer === 'DONE' ? ref(a.performer) : undefined,
      laterDate: a.answer === 'LATER' ? a.laterDate : undefined,
      reason: a.answer === 'NOT_DONE' ? (a.reason || undefined) : undefined,
    })),
  });
  return d?.answerOpenServices;
}

/** «رزرو همهٔ جلسات» preview: every waiting session from `firstAt` (epoch ms), every `intervalDays`. */
export async function planAllSessions({ orderId, firstAt, intervalDays, performer }) {
  const d = await bffQuery(PLAN_ALL_SESSIONS, {
    orderId, firstAt: String(firstAt), intervalDays: Number(intervalDays), performer: ref(performer),
  });
  return d?.planAllSessions || [];
}

/** «رزرو همهٔ جلسات»: all or nothing — the BFF cancels what it made when one fails. */
export async function bookAllSessions({ orderId, slots }) {
  const d = await bffMutation(BOOK_ALL_SESSIONS, {
    orderId,
    slots: slots.map((s) => ({ sessionNo: s.sessionNo, at: String(s.at), performer: ref(s.performer) })),
  });
  return d?.bookAllSessions;
}

/** «همگام‌سازی»: re-send a session to accounting. */
export async function resyncSessionCharge(sessionId) {
  const d = await bffMutation(RESYNC_SESSION_CHARGE, { sessionId });
  return d?.resyncSessionCharge;
}

/** The patient's open مراجعه(s) at this clinic — what «افزودن به این مراجعه» attaches to. */
export async function fetchPatientOpenVisits(patientId) {
  const d = await bffQuery(PATIENT_OPEN_VISITS, { patientId });
  return d?.patientOpenVisits || [];
}

/**
 * The open مراجعه(s) of each of `patientIds` → `{ byPatient: Map, loaded }`.
 * A failed read is an empty list for that patient: the only thing it decides is
 * whether «افزودن به این مراجعه» is offered, and hidden = not applicable.
 */
export function usePatientOpenVisits(patientIds, { enabled = true, refreshKey = 0 } = {}) {
  const ids = useMemo(() => [...new Set((patientIds || []).filter(Boolean))].sort(), [patientIds]);
  const key = ids.join(',');
  const [byPatient, setByPatient] = useState(() => new Map());
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!enabled || !ids.length) {
      setByPatient((prev) => (prev.size ? new Map() : prev));
      setLoaded(true);
      return undefined;
    }
    let live = true;
    Promise.all(ids.map((pid) => fetchPatientOpenVisits(pid).then((v) => [pid, v]).catch(() => [pid, []])))
      .then((pairs) => {
        if (!live) return;
        setByPatient(new Map(pairs));
        setLoaded(true);
      });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, enabled, refreshKey]);

  return { byPatient, loaded };
}

/** The items of one مراجعه — the settle drawer's checkout list. Four states. */
export function useAppointmentServices(appointmentId, { enabled = true } = {}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(!!(enabled && appointmentId));
  const [error, setError] = useState(null);

  const reload = useCallback(async () => {
    if (!enabled || !appointmentId) { setItems([]); setError(null); setLoading(false); return []; }
    setLoading(true);
    try {
      const d = await bffQuery(APPOINTMENT_SERVICES, { appointmentId });
      const rows = d?.appointmentServices || [];
      setItems(rows);
      setError(null);
      return rows;
    } catch (e) {
      setItems([]);
      setError(e?.message || 'appointmentServices');
      return null;
    } finally {
      setLoading(false);
    }
  }, [appointmentId, enabled]);

  useEffect(() => { reload(); }, [reload]);

  return { items, loading, error, reload };
}

/**
 * The items of every مراجعه on the day list → `Map(appointmentId → { services,
 * openServices })`, one read for the whole day (the BFF batches it). Re-read
 * whenever the day's rows are — `rowsKey` changes on every poll that moved one.
 */
export function useDayVisitServices(dateAnchor, rowsKey, { enabled = true } = {}) {
  const [byAppointment, setByAppointment] = useState(() => new Map());
  const [loading, setLoading] = useState(!!enabled);
  const [error, setError] = useState(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!enabled) { setLoading(false); return undefined; }
    let live = true;
    bffQuery(DAY_VISIT_SERVICES, { date: dateAnchor != null ? String(dateAnchor) : null })
      .then((d) => {
        if (!live) return;
        const m = new Map();
        for (const a of d?.appointments || []) {
          if (a?.id) m.set(a.id, { services: a.services || [], openServices: a.openServices || 0 });
        }
        setByAppointment(m);
        setError(null);
      })
      .catch((e) => { if (live) setError(e?.message || 'appointments.services'); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [dateAnchor, rowsKey, enabled, tick]);

  const reload = useCallback(() => setTick((n) => n + 1), []);
  return { byAppointment, loading, error, reload };
}
